import { create } from 'zustand'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { db, generateId, nowISO, todayDate, queueSync, type LocalBodyWeightLog } from '@/lib/db'

interface BodyWeightState {
  logs: LocalBodyWeightLog[]
  loading: boolean
  error: string | null

  // Actions
  loadLogs: (userId: string, days?: number) => Promise<void>
  addLog: (userId: string, weightKg: number, bodyFatPct?: number, notes?: string) => Promise<void>
  updateLog: (id: string, updates: Partial<LocalBodyWeightLog>) => Promise<void>
  deleteLog: (id: string) => Promise<void>
  getTodayLog: () => LocalBodyWeightLog | undefined
  getLatestWeight: () => number | undefined
}

export const useBodyWeightStore = create<BodyWeightState>()((set, get) => ({
  logs: [],
  loading: false,
  error: null,

  loadLogs: async (userId: string, days = 30) => {
    set({ loading: true, error: null })

    try {
      // Calculate date range
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      const startDateStr = startDate.toISOString().split('T')[0]

      // Always load from IndexedDB first (offline-first approach)
      const localLogs = await db.bodyWeightLogs
        .where('user_id')
        .equals(userId)
        .and(log => log.date >= startDateStr)
        .toArray()

      // Sort by date descending
      localLogs.sort((a, b) => b.date.localeCompare(a.date))

      // Set initial state from local data immediately
      set({ logs: localLogs, loading: false })

      // Then sync with Supabase in background if configured
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from('body_weight_logs')
            .select('*')
            .eq('user_id', userId)
            .gte('date', startDateStr)
            .order('date', { ascending: false })

          if (!error && data && data.length > 0) {
            // Convert to local format
            const remoteLogs: LocalBodyWeightLog[] = data.map(row => ({
              id: row.id,
              user_id: row.user_id,
              date: row.date,
              weight_kg: parseFloat(row.weight_kg),
              body_fat_pct: row.body_fat_pct ? parseFloat(row.body_fat_pct) : undefined,
              notes: row.notes ?? undefined,
              sync_status: 'synced' as const,
              created_at: row.created_at,
              updated_at: row.created_at,
            }))

            // Merge: keep local pending items not on remote
            const pendingLocal = localLogs.filter(l => l.sync_status === 'pending')
            const remoteIds = new Set(remoteLogs.map(l => l.id))
            const pendingNotOnRemote = pendingLocal.filter(l => !remoteIds.has(l.id))

            // Combine remote logs with any pending local-only logs
            const mergedLogs = [...remoteLogs, ...pendingNotOnRemote]
              .sort((a, b) => b.date.localeCompare(a.date))

            // Cache remote logs in IndexedDB
            await db.bodyWeightLogs.bulkPut(remoteLogs)

            // Update state with merged data
            set({ logs: mergedLogs })
          }
        } catch (syncError) {
          // Supabase sync failed, but we already have local data - that's fine
          console.warn('Supabase sync failed, using local data:', syncError)
        }
      }
    } catch (error) {
      console.error('Load body weight logs error:', error)
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load weight logs',
      })
    }
  },

  addLog: async (userId: string, weightKg: number, bodyFatPct?: number, notes?: string) => {
    const today = todayDate()
    const now = nowISO()

    // Check if we already have a log for today
    const existingLog = get().logs.find(log => log.date === today)
    if (existingLog) {
      // Update existing log instead
      await get().updateLog(existingLog.id, { weight_kg: weightKg, body_fat_pct: bodyFatPct, notes })
      return
    }

    const newLog: LocalBodyWeightLog = {
      id: generateId(),
      user_id: userId,
      date: today,
      weight_kg: weightKg,
      body_fat_pct: bodyFatPct,
      notes,
      sync_status: 'pending',
      created_at: now,
      updated_at: now,
    }

    // Optimistic update
    set(state => ({
      logs: [newLog, ...state.logs],
    }))

    try {
      // Save to IndexedDB
      await db.bodyWeightLogs.add(newLog)

      // Try to sync to Supabase
      if (isSupabaseConfigured()) {
        const { error } = await supabase.from('body_weight_logs').insert({
          id: newLog.id,
          user_id: userId,
          date: today,
          weight_kg: weightKg,
          body_fat_pct: bodyFatPct ?? null,
          notes: notes ?? null,
        })

        if (error) {
          // Queue for later sync
          await queueSync('body_weight_logs', newLog.id, 'insert', newLog as unknown as Record<string, unknown>)
        } else {
          // Mark as synced
          await db.bodyWeightLogs.update(newLog.id, { sync_status: 'synced' })
          set(state => ({
            logs: state.logs.map(log =>
              log.id === newLog.id ? { ...log, sync_status: 'synced' as const } : log
            ),
          }))
        }
      }
    } catch (error) {
      console.error('Add body weight log error:', error)
      // Data is still saved locally, queue for sync
      await queueSync('body_weight_logs', newLog.id, 'insert', newLog as unknown as Record<string, unknown>)
    }
  },

  updateLog: async (id: string, updates: Partial<LocalBodyWeightLog>) => {
    const now = nowISO()
    const updatedFields = { ...updates, updated_at: now, sync_status: 'pending' as const }

    // Optimistic update
    set(state => ({
      logs: state.logs.map(log =>
        log.id === id ? { ...log, ...updatedFields } : log
      ),
    }))

    try {
      // Update IndexedDB
      await db.bodyWeightLogs.update(id, updatedFields)

      // Try to sync to Supabase
      if (isSupabaseConfigured()) {
        const { error } = await supabase
          .from('body_weight_logs')
          .update({
            weight_kg: updates.weight_kg,
            body_fat_pct: updates.body_fat_pct ?? null,
            notes: updates.notes ?? null,
          })
          .eq('id', id)

        if (error) {
          await queueSync('body_weight_logs', id, 'update', updates)
        } else {
          await db.bodyWeightLogs.update(id, { sync_status: 'synced' })
          set(state => ({
            logs: state.logs.map(log =>
              log.id === id ? { ...log, sync_status: 'synced' as const } : log
            ),
          }))
        }
      }
    } catch (error) {
      console.error('Update body weight log error:', error)
      await queueSync('body_weight_logs', id, 'update', updates)
    }
  },

  deleteLog: async (id: string) => {
    // Optimistic update
    set(state => ({
      logs: state.logs.filter(log => log.id !== id),
    }))

    try {
      await db.bodyWeightLogs.delete(id)

      if (isSupabaseConfigured()) {
        const { error } = await supabase
          .from('body_weight_logs')
          .delete()
          .eq('id', id)

        if (error) {
          await queueSync('body_weight_logs', id, 'delete')
        }
      }
    } catch (error) {
      console.error('Delete body weight log error:', error)
      await queueSync('body_weight_logs', id, 'delete')
    }
  },

  getTodayLog: () => {
    const today = todayDate()
    return get().logs.find(log => log.date === today)
  },

  getLatestWeight: () => {
    const logs = get().logs
    if (logs.length === 0) return undefined
    return logs[0].weight_kg
  },
}))
